import React, { useState } from "react";
import CrossAttachmentAssessment from "./CrossAttachmentAssessment";
import axios from "./../../axios";
import { toast } from "react-toastify";
import assets from "../../assets/assets";
import ComponentType from "../../Enum/ComponentTypeEnum";
import { Plus, Sparkles, Trash2, Info, CheckCircle2 } from "lucide-react";
import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Tooltip,
  InputAdornment,
} from "@mui/material";

const CreateUploadType = ({
  question,
  nextQuestion,
  setQuestions,
  componentType,
  hasDescriptiveAutoAssessment,
  selectedBox,
}) => {
  let teacher_id = JSON.parse(localStorage.getItem("Teacher_id"));
  const [isValidFileType, setIsValidFileType] = useState(true);
  const [loader, setLoader] = useState(false);

  const isQuestionBank = componentType === ComponentType.QUESTION_BANK;

  const isExpectedAnswerMissing =
    !question?.subjective_evaluation?.expectedAnswer ||
    question.subjective_evaluation.expectedAnswer.trim() === "";

  const errorInputStyle = {
    border: "1px solid #FCA5A5",
    backgroundColor: "#FEF2F2",
  };
  const normalInputStyle = {
    border: "1px solid #D1D5DB",
    backgroundColor: "#fff",
  };

  // ==========================================
  // 👈 FILE VALIDATION — purana logic wapas
  // ==========================================
  const validateFileType = (file) => {
    const isAssessmentOrQB =
      componentType === ComponentType.ASSESSMENT ||
      componentType === ComponentType.QUESTION_BANK;

    // ASSESSMENT / QB ke liye sirf images + pdf
    // HOMEWORK ke liye doc/docx bhi allowed
    const allowedExtensions = isAssessmentOrQB
      ? /(\.jpg|\.jpeg|\.png|\.pdf)$/i
      : /(\.jpg|\.jpeg|\.png|\.pdf|\.doc|\.docx)$/i;

    if (
      !allowedExtensions.test(file.name) ||
      question?.attachments?.length >= 5
    ) {
      setIsValidFileType(false);
      return false;
    }
    setIsValidFileType(true);
    return true;
  };

  const updateSubjective = (patch) => {
    setQuestions((prev) => {
      const isDisabling = patch.enabled === false;
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...(prev[nextQuestion].subjective_evaluation || {
            enabled: true,
            expectedAnswer: "",
            rubric: [],
          }),
          ...patch,
          ...(isQuestionBank ? { enabled: false } : {}),
          ...(isDisabling && !isQuestionBank ? { rubric: [] } : {}),
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleExpectedAnswerChange = (value) => {
    setQuestions((prev) => {
      const currentSubjective = prev[nextQuestion].subjective_evaluation || {
        enabled: isQuestionBank ? false : false,
        expectedAnswer: "",
        rubric: [],
      };
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...currentSubjective,
          expectedAnswer: value,
          ...(isQuestionBank ? { enabled: false } : {}),
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleRubricChange = (index, field, value) => {
    if (!question?.subjective_evaluation?.enabled && !isQuestionBank) return;
    setQuestions((prev) => {
      const current = prev[nextQuestion].subjective_evaluation || {
        enabled: true,
        expectedAnswer: "",
        rubric: [],
      };
      const updatedRubric = current.rubric.map((r, i) =>
        i === index
          ? { ...r, [field]: field === "maxMarks" ? Number(value) || 0 : value }
          : r,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...current,
          enabled: isQuestionBank ? false : true,
          rubric: updatedRubric,
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleAddRubric = () => {
    if (!question?.subjective_evaluation?.enabled && !isQuestionBank) return;
    setQuestions((prev) => {
      const current = prev[nextQuestion].subjective_evaluation || {
        enabled: false,
        expectedAnswer: "",
        rubric: [],
      };
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...current,
          enabled: isQuestionBank ? false : true,
          rubric: [...current.rubric, { criterion: "", maxMarks: 0 }],
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleRemoveRubric = (index) => {
    if (!question?.subjective_evaluation?.enabled && !isQuestionBank) return;
    setQuestions((prev) => {
      const current = prev[nextQuestion].subjective_evaluation || {
        enabled: true,
        expectedAnswer: "",
        rubric: [],
      };
      const newRubric = current.rubric.filter((_, i) => i !== index);
      const shouldBeEnabled = isQuestionBank ? false : newRubric.length > 0;
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...current,
          enabled: shouldBeEnabled,
          rubric: newRubric,
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleReasonChange = (index, value) => {
    setQuestions((prev) => {
      const updatedCorrectAnswer = prev[nextQuestion].correct_answers.map(
        (answer, i) =>
          i === index
            ? {
                ...answer,
                reason_for_answer: {
                  ...answer.reason_for_answer,
                  explanation: value,
                },
              }
            : answer,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleAttachmentChange = async (index, file) => {
    if (file) {
      const isUnder10MB = file.size < 10 * 1024 * 1024;
      if (!isUnder10MB) {
        toast.error("File must be under 10MB.");
        return;
      }

      // 👈 validateFileType check wapas
      if (!validateFileType(file)) {
        return;
      }

      try {
        setLoader(true);
        let formData = new FormData();
        formData.append("name", file.name);
        formData.append("uploadQuestionAttachment", file);
        let res = await axios.post(
          `/api/v1/question/uploadAttachment/teacherId/${teacher_id}`,
          formData,
        );
        res && setLoader(false);
        res &&
          setQuestions((prev) => {
            const updatedCorrectAnswer = prev[nextQuestion].correct_answers.map(
              (answer, i) =>
                i === index
                  ? {
                      ...answer,
                      reason_for_answer: {
                        ...answer.reason_for_answer,
                        attachment: res.data.data,
                      },
                    }
                  : answer,
            );
            const updatedQuestion = {
              ...prev[nextQuestion],
              correct_answers: updatedCorrectAnswer,
              is_question_edited: true,
            };
            const newQuestions = [...prev];
            newQuestions[nextQuestion] = updatedQuestion;
            return newQuestions;
          });
      } catch (error) {
        setLoader(false);
        console.log(error);
      }
    }
  };

  const isAutoEvaluateMode =
    selectedBox === "auto-evaluate" ||
    selectedBox?.autoEvaluate === true ||
    selectedBox?.isAutoEvaluate === true ||
    selectedBox?.isAutoEval === true;
  const showSubjectiveAutoEval =
    hasDescriptiveAutoAssessment === true && isAutoEvaluateMode;
  const enabled = isQuestionBank
    ? false
    : (question?.subjective_evaluation?.enabled ?? true);
  const rubric = question?.subjective_evaluation?.rubric || [];
  const totalRubricMarks = rubric.reduce(
    (sum, r) => sum + (Number(r.maxMarks) || 0),
    0,
  );

  const ACCENT = "#F28C28";
  const ACCENT_SOFT = "#FFF7ED";
  const BORDER = "#E5E7EB";
  const TEXT_PRIMARY = "#1f2937";
  const TEXT_SECONDARY = "#6B7280";
  const BG_SOFT = "#F9FAFB";

  return (
    <>
      {showSubjectiveAutoEval ? (
        <Box className="col-12 ml-1" sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.2,
                background: BG_SOFT,
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "6px",
                    background: ACCENT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  <Sparkles size={14} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      color: TEXT_PRIMARY,
                      lineHeight: 1.2,
                    }}
                  >
                    {isQuestionBank
                      ? "Subjective Question Type"
                      : "Subjective Auto-Evaluation Setup"}
                  </Typography>
                </Box>
              </Box>
              <Chip
                icon={<Sparkles size={12} color={ACCENT} />}
                label="AI"
                size="small"
                sx={{
                  background: ACCENT_SOFT,
                  color: ACCENT,
                  fontWeight: 600,
                  fontSize: "10px",
                  border: `1px solid ${ACCENT}`,
                  height: "22px",
                  "& .MuiChip-icon": { ml: "4px" },
                }}
              />
            </Box>

            <Box sx={{ p: 2 }}>
              {!isQuestionBank && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.2,
                    mb: 2,
                    border: `1px solid ${enabled ? ACCENT : BORDER}`,
                    background: enabled ? ACCENT_SOFT : BG_SOFT,
                    borderRadius: "6px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {enabled ? (
                      <CheckCircle2 size={16} color={ACCENT} />
                    ) : (
                      <Info size={16} color={TEXT_SECONDARY} />
                    )}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          color: TEXT_PRIMARY,
                        }}
                      >
                        Enable Auto-Evaluation (AI-Assisted)
                      </Typography>
                      <Typography
                        sx={{ fontSize: "11px", color: TEXT_SECONDARY }}
                      >
                        {enabled
                          ? "AI will evaluate against the rubric."
                          : "Turn on to let AI score."}
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={enabled}
                    onChange={() => updateSubjective({ enabled: !enabled })}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { backgroundColor: ACCENT },
                    }}
                  />
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: TEXT_PRIMARY,
                    }}
                  >
                    Marking Rubric / Criteria
                  </Typography>
                  <Chip
                    label={`${rubric.length} criteria`}
                    size="small"
                    sx={{
                      background: BG_SOFT,
                      color: TEXT_SECONDARY,
                      fontSize: "10px",
                      height: "20px",
                      opacity: enabled || isQuestionBank ? 1 : 0.6,
                    }}
                  />
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: "6px",
                    overflow: "hidden",
                    opacity: enabled || isQuestionBank ? 1 : 0.6,
                    pointerEvents: enabled || isQuestionBank ? "auto" : "none",
                  }}
                >
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow sx={{ background: BG_SOFT }}>
                        <TableCell
                          sx={{
                            width: 40,
                            fontWeight: 600,
                            fontSize: "11px",
                            color: TEXT_SECONDARY,
                            borderBottom: `1px solid ${BORDER}`,
                            py: 1,
                          }}
                        ></TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            color: TEXT_SECONDARY,
                            borderBottom: `1px solid ${BORDER}`,
                            py: 1,
                          }}
                        >
                          CRITERION *
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 140,
                            fontWeight: 600,
                            fontSize: "11px",
                            color: TEXT_SECONDARY,
                            borderBottom: `1px solid ${BORDER}`,
                            py: 1,
                          }}
                        >
                          MAX MARKS *
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 40,
                            borderBottom: `1px solid ${BORDER}`,
                            py: 1,
                          }}
                        ></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rubric.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              textAlign: "center",
                              py: 2,
                              color: TEXT_SECONDARY,
                              fontSize: "11px",
                              fontStyle: "italic",
                            }}
                          >
                            No rubric added yet.
                          </TableCell>
                        </TableRow>
                      )}
                      {rubric.map((r, index) => {
                        const isCriterionMissing =
                          !r.criterion || r.criterion.trim() === "";
                        const isMaxMarksMissing =
                          !r.maxMarks || r.maxMarks <= 0;
                        return (
                          <TableRow
                            key={index}
                            sx={{ "&:hover": { background: "#fbfcfe" } }}
                          >
                            <TableCell
                              sx={{
                                textAlign: "center",
                                fontWeight: 600,
                                fontSize: "12px",
                                color: TEXT_SECONDARY,
                                borderBottom: `1px solid ${BORDER}`,
                                py: 0.5,
                              }}
                            >
                              {index + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${BORDER}`,
                                py: 0.5,
                              }}
                            >
                              <TextField
                                fullWidth
                                variant="standard"
                                placeholder="Enter criterion..."
                                value={r.criterion}
                                onChange={(e) =>
                                  handleRubricChange(
                                    index,
                                    "criterion",
                                    e.target.value,
                                  )
                                }
                                disabled={!enabled && !isQuestionBank}
                                InputProps={{
                                  disableUnderline: true,
                                  style: {
                                    fontSize: "12.5px",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    ...(isCriterionMissing
                                      ? errorInputStyle
                                      : {}),
                                  },
                                }}
                                sx={{ "& .MuiInputBase-input": { py: 0.5 } }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${BORDER}`,
                                py: 0.5,
                              }}
                            >
                              <TextField
                                type="number"
                                variant="outlined"
                                size="small"
                                value={r.maxMarks}
                                onChange={(e) =>
                                  handleRubricChange(
                                    index,
                                    "maxMarks",
                                    e.target.value,
                                  )
                                }
                                disabled={!enabled && !isQuestionBank}
                                inputProps={{ min: 0 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography
                                        sx={{
                                          color: isMaxMarksMissing
                                            ? "#DC2626"
                                            : ACCENT,
                                          fontWeight: 700,
                                          fontSize: "11px",
                                        }}
                                      >
                                        [
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography
                                        sx={{
                                          color: isMaxMarksMissing
                                            ? "#DC2626"
                                            : ACCENT,
                                          fontWeight: 700,
                                          fontSize: "10px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Marks]
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "4px",
                                    background: isMaxMarksMissing
                                      ? "#FEF2F2"
                                      : BG_SOFT,
                                    fontSize: "12px",
                                    height: "28px",
                                    "& fieldset": {
                                      borderColor: isMaxMarksMissing
                                        ? "#FCA5A5"
                                        : BORDER,
                                    },
                                    "&:hover fieldset": { borderColor: ACCENT },
                                    "&.Mui-focused fieldset": {
                                      borderColor: ACCENT,
                                    },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "center",
                                borderBottom: `1px solid ${BORDER}`,
                                py: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveRubric(index)}
                                disabled={!enabled && !isQuestionBank}
                                sx={{
                                  color: "#DC2626",
                                  "&:hover": {
                                    background: "rgba(220,53,69,0.08)",
                                  },
                                }}
                              >
                                <Trash2 size={14} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {rubric.length > 0 && (
                        <TableRow sx={{ background: ACCENT_SOFT }}>
                          <TableCell></TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              color: TEXT_PRIMARY,
                              py: 0.5,
                            }}
                          >
                            Total Rubric Marks
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "12px",
                              color: ACCENT,
                              py: 0.5,
                            }}
                          >
                            [ {totalRubricMarks} ]
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                <Button
                  startIcon={<Plus size={14} />}
                  onClick={handleAddRubric}
                  disabled={!enabled && !isQuestionBank}
                  variant="outlined"
                  sx={{
                    mt: 1.5,
                    borderColor: ACCENT,
                    color: ACCENT,
                    borderRadius: "4px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "11px",
                    px: 1.5,
                    py: 0.4,
                    "&:hover": { borderColor: ACCENT, background: ACCENT_SOFT },
                    "&.Mui-disabled": {
                      borderColor: BORDER,
                      color: TEXT_SECONDARY,
                    },
                  }}
                >
                  Add Rubric Criterion
                </Button>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: TEXT_PRIMARY,
                    }}
                  >
                    Expected Answer / Key Concepts *
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="e.g. The child says 'Good Morning' to the sun..."
                  value={question?.subjective_evaluation?.expectedAnswer || ""}
                  onChange={(e) => handleExpectedAnswerChange(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      fontSize: "12.5px",
                      background: isExpectedAnswerMissing ? "#FEF2F2" : "#fff",
                      "& fieldset": {
                        borderColor: isExpectedAnswerMissing
                          ? "#FCA5A5"
                          : BORDER,
                      },
                      "&:hover fieldset": { borderColor: ACCENT },
                      "&.Mui-focused fieldset": { borderColor: ACCENT },
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Box>
      ) : (
        <div className="col-12 ml-1 mt-3">
          <div
            className="poppins-semibold"
            style={{ fontSize: "12px", color: "#374151" }}
          >
            Add correct answer *
          </div>
          {question.correct_answers.length > 0 &&
            question.correct_answers.map((cAnswer, index) => (
              <div key={index} className="mt-2">
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ width: "100%" }}
                >
                  <textarea
                    onChange={(e) => handleReasonChange(index, e.target.value)}
                    className="rounded-2 p-2"
                    placeholder="Type correct answer here..."
                    style={{
                      flex: 1,
                      fontSize: "13px",
                      outline: "none",
                      border: !cAnswer.reason_for_answer.explanation
                        ? "1px solid #FCA5A5"
                        : "1px solid #D1D5DB",
                      backgroundColor: !cAnswer.reason_for_answer.explanation
                        ? "#FEF2F2"
                        : "#fff",
                      resize: "vertical",
                      minHeight: "36px",
                    }}
                    value={cAnswer.reason_for_answer.explanation || ""}
                  />
                  <div>
                    <input
                      onChange={(e) =>
                        handleAttachmentChange(index, e.target.files[0])
                      }
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      id={`attachFileForReason${nextQuestion}-${index}`}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor={`attachFileForReason${nextQuestion}-${index}`}
                      style={{
                        border: "1px dashed #D1D5DB",
                        backgroundColor: "#F9FAFB",
                        cursor: "pointer",
                        width: "32px",
                        height: "32px",
                      }}
                      className="rounded-2 d-flex align-items-center justify-content-center"
                    >
                      <img src={assets.UploadIcon} width="16px" alt="Attach" />
                    </label>
                  </div>
                </div>
                {cAnswer.reason_for_answer.attachment && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "98%",
                    }}
                    className="mt-2"
                  >
                    <CrossAttachmentAssessment
                      attachment={cAnswer.reason_for_answer.attachment}
                      isReasonForAnswer={true}
                      isEditable={true}
                      setQuestions={setQuestions}
                      question={question}
                      nextQuestion={nextQuestion}
                      componentType={componentType}
                    />
                  </div>
                )}
                {loader && (
                  <img
                    style={{ width: "20px", height: "20px" }}
                    src="/gif/Loader.gif"
                    alt="loading"
                  />
                )}
                {!isValidFileType && (
                  <p
                    style={{ color: "red", fontSize: "11px", marginTop: "4px" }}
                  >
                    Please upload a valid file (pdf, jpg, jpeg, png
                    {componentType === ComponentType.HOMEWORK
                      ? ", doc, docx"
                      : ""}
                    ). Max 5 files.
                  </p>
                )}
              </div>
            ))}
        </div>
      )}
    </>
  );
};

export default CreateUploadType;
